import axios from "axios";
import * as authTypes from "../constants/authTypes";
import { vndEvent } from "../model/event/vndEvent";

const { get, patch, post } = axios;
const deleteAction = axios.delete;

const { REACT_APP_AUTH, REACT_APP_BA_LOGIN_URL } = process.env;
const API_CREDENTIALS_ENABLED = process.env.REACT_APP_API_WITH_CREDENTIALS === "true";
const clientSettingsApiUrl = process.env.REACT_APP_CLIENT_SETTINGS_API_URL;

let clientSettings: any;
let mapCenter: string | undefined;

export const setMapCenter = (center: string | undefined) => {
  mapCenter = center;
};

axios.defaults.withCredentials = API_CREDENTIALS_ENABLED;

axios.interceptors.request.use((config) => {
  if (mapCenter) {
    config.headers = {
      ...config.headers,
      "lat-lng": mapCenter,
    };
  }

  return config;
});

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      handleUnauthorised();
    }

    return Promise.reject(error);
  },
);

const handleUnauthorised = () => {
  if (
    REACT_APP_AUTH === authTypes.AUTH0 ||
    REACT_APP_AUTH === authTypes.B2C ||
    REACT_APP_AUTH === authTypes.B2B
  ) {
    window.location.href = "/unauthorised";
  }
};

const getBounds = (bounds: any) => {
  return {
    southLat: bounds.getSouth(),
    westLng: bounds.getWest(),
    northLat: bounds.getNorth(),
    eastLng: bounds.getEast(),
  };
};

const postHeaders = (bearerToken: string | null) => {
  let headers: any = {};

  headers.headers = {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  };

  headers.withCredentials = API_CREDENTIALS_ENABLED;

  const authBearerToken = buildBearerToken(bearerToken ?? "");

  if (authBearerToken) {
    headers.headers = {
      ...headers.headers,
      Authorization: authBearerToken,
    };
  }

  return headers;
};

const authenticateUser = (user: string, password: string) => {
  let headers: any = {};

  headers.headers = {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  };

  headers.withCredentials = API_CREDENTIALS_ENABLED;

  const token = `${user}:${password}`;
  const hash = window.btoa(token);

  sessionStorage.setItem("token", window.btoa(user));

  if (REACT_APP_AUTH === authTypes.BASIC) {
    headers.headers = {
      ...headers.headers,
      Authorization: `Basic ${hash}`,
    };
  }

  return headers;
};

const getHeaders = (bearerToken: string | null) => {
  let headers: any = {};

  headers.withCredentials = API_CREDENTIALS_ENABLED;

  const authBearerToken = buildBearerToken(bearerToken ?? "");

  if (authBearerToken) {
    headers.headers = {
      ...headers.headers,
      Authorization: authBearerToken,
    };
  }

  return headers;
};

export const buildBearerToken = (bearerToken: string) => {
  if (!bearerToken) {
    return null;
  }

  switch (process.env.REACT_APP_AUTH) {
    case authTypes.ANONYMOUS:
      return null;
    case authTypes.BASIC:
      return `Basic ${bearerToken}`;
    case authTypes.B2C:
    case authTypes.B2B:
    case authTypes.AUTH0:
      return `Bearer ${bearerToken}`;
    default:
      return `Bearer ${bearerToken}`;
  }
};

export const authorise = async (token: string | null) => {
  const headers = postHeaders(token);
  return post(`${clientSettings?.urls.endPoints.authorised}`, {}, headers);
};

export const basicAuthorise = async (user: string, password: string) => {
  const headers = authenticateUser(user, password);
  return post(REACT_APP_BA_LOGIN_URL!, {}, headers);
};

export const getReference = async (token: string | null) => {
  const headers = getHeaders(token);
  return get(`${clientSettings?.urls.endPoints.reference}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const getDefaultReference = async (token: string | null) => {
  const headers = getHeaders(token);
  return get(`${clientSettings?.urls.endPoints.defaultReference}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const getSpecificReference = async (token: string | null, client: any, version: any) => {
  const headers = getHeaders(token);
  let url = clientSettings.urls.endPoints.specificReference;
  url = url.replace("{client}", encodeURIComponent(client));
  url = url.replace("{version}", encodeURIComponent(version));
  return get(url, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const getClientLatestReference = async (token: string | null, client: any) => {
  const headers = getHeaders(token);
  let url = clientSettings.urls.endPoints.clientLatestReference;
  url = url.replace("{client}", encodeURIComponent(client));
  return get(url, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const viewConfig = async (token: string | null, client: any, version: any) => {
  const headers = getHeaders(token);
  let url = clientSettings.urls.endPoints.viewSpecificConfig;
  url = url.replace("{client}", encodeURIComponent(client));
  url = url.replace("{version}", encodeURIComponent(version));

  return get(url, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const listConfigs = async (token: string | null) => {
  const headers = getHeaders(token);
  return get(`${clientSettings?.urls.endPoints.listConfigs}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const translateConfigToUI = async (token: string | null, raw: any) => {
  const options = await getHeaders(token);
  return post(
    `${clientSettings.urls.endPoints.translateRawConfigToUI}`,
    { files: [{ contents: raw }] },
    options,
  )
    .then((result) => result.data)
    .catch((error) => error);
};

export const translateConfigToView = async (token: string | null, raw: any) => {
  const options = await getHeaders(token);
  return post(
    `${clientSettings.urls.endPoints.translateRawConfigToView}`,
    { files: [{ contents: raw }] },
    options,
  )
    .then((result) => result.data)
    .catch((error) => error);
};

export const getStudyFilters = async (token: string | null) => {
  const headers = getHeaders(token);

  return get(`${clientSettings?.urls.endPoints.studyFilters}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const getClientSettings = async (token: string | null) => {
  const headers = getHeaders(token);
  return get(`${clientSettingsApiUrl}`, headers)
    .then((result) => {
      clientSettings = result.data;
      return result.data;
    })
    .catch((error) => error);
};

export const postAssessments = async (
  token: string | null,
  obj: any,
  { assessmentExporting }: any,
) => {
  const options = postHeaders(token);
  if (assessmentExporting) {
    options.headers["Accept"] = "application/vnd.ms-excel";
    options.responseType = "blob";
  }
  return post(`${clientSettings.urls.endPoints.assessment}`, obj, options);
};

export const loadFile = async (token: string | null, studyId: any) => {
  const headers = getHeaders(token);

  let url = clientSettings.urls.endPoints.studyLoad;
  url = url.replace("{studyId}", encodeURIComponent(studyId));

  return get(`${url}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const loadFiles = async (token: string | null) => {
  const headers = getHeaders(token);

  return get(`${clientSettings.urls.endPoints.studyList}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const translateWinDebutToStudy = async (token: string | null, obj: any, config: any) => {
  const options = await postHeaders(token);
  if (config !== "auto") {
    options.headers["Configuration-Client"] = config.clientName;
    options.headers["Configuration-Version"] = config.versionNumber;
  } else {
    options.headers["Configuration-Auto"] = true;
  }
  return post(`${clientSettings.urls.endPoints.winDebutToStudy}`, obj, options);
};

export const translateXmlToStudy = async (token: string | null, obj: any) => {
  const options = postHeaders(token);
  options.headers["Configuration-Auto"] = true;
  return post(`${clientSettings.urls.endPoints.smallworldToStudy}`, obj, options);
};

export const saveNetwork = async (token: string | null, obj: any) => {
  const headers = postHeaders(token);
  return post(`${clientSettings.urls.endPoints.studySave}`, obj, headers);
};

export const deleteNetwork = async (token: string | null, studyId: any) => {
  const headers = postHeaders(token);

  let url = clientSettings.urls.endPoints.studyDelete;
  url = url.replace("{studyId}", studyId);

  return deleteAction(url, headers);
};

export const archiveNetwork = async (token: string | null, studyId: any, obj: any) => {
  const headers = postHeaders(token);

  let url = clientSettings.urls.endPoints.studyArchive;
  url = url.replace("{studyId}", studyId);

  return patch(url, obj, headers);
};

export const updateNetwork = async (token: string | null, studyId: any, obj: any) => {
  const headers = postHeaders(token);

  let url = clientSettings.urls.endPoints.studyUpdateContent;
  url = url.replace("{studyId}", studyId);

  return patch(url, obj, headers);
};

export const saveFeedback = async (token: string | null, obj: any) => {
  const headers = postHeaders(token);
  return post(`${clientSettings.urls.endPoints.feedbackSave}`, obj, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const loadNetworkJson = async (
  token: string | null,
  substationNumber: any,
  feeders: any,
  mains: any,
  electricOffice: any,
  useAntData: any,
) => {
  const headers = getHeaders(token);
  const feeders_ = feeders.length ? `${feeders.join()}` : "";
  let mains_ = "";
  let electricOffice_ = "";

  if (feeders.length) {
    mains_ = mains ? `${mains}` : "";
  } else {
    mains_ = mains ? `${mains}` : "";
  }

  if (feeders.length || mains.length) {
    electricOffice_ = electricOffice ? `${electricOffice}` : "";
  } else {
    electricOffice_ = electricOffice ? `${electricOffice}` : "";
  }

  let url = clientSettings.urls.endPoints.networkLoad;
  url = url.replace("{substationNumber}", substationNumber);
  url = url.replace("{feeders}", feeders_);
  url = url.replace("{mainsOnly}", mains_);
  url = url.replace("{electricOfficeOnly}", electricOffice_);
  url = url.replace("{useGeometry}", !(useAntData && clientSettings.features.DataBasedANTEnabled));

  return get(`${url}`, headers)
    .then((result) => {
      return result;
    })
    .catch((error) => error);
};

export const loadFeederList = async (
  token: string | null,
  substationNumber: any,
  useAntData: any,
) => {
  const headers = getHeaders(token);

  let url = clientSettings.urls.endPoints.networkLoadFeeders;
  url = url.replace("{substationNumber}", substationNumber);
  url = url.replace("{useGeometry}", !(useAntData && clientSettings.features.DataBasedANTEnabled));

  return get(`${url}`, headers)
    .then((result) => {
      return result;
    })
    .catch((error) => {
      let errorText = "There was a problem finding the network.";
      const errorDescription = error?.response?.data?.errors?.id[0];
      if (errorDescription) {
        errorText = errorText + " " + errorDescription;
      }

      return {
        data: {
          network: {
            messages: [
              {
                description: errorText,
                level: 4,
              },
            ],
          },
        },
      };
    });
};

export const getNearbySubstations = async (
  token: string | null,
  lat: any,
  lng: any,
  distanceInMeters: any,
) => {
  const headers = getHeaders(token);

  let url = clientSettings.urls.endPoints.networkNearbySubstations;
  url = url.replace("{lat}", lat);
  url = url.replace("{lng}", lng);
  url = url.replace("{distanceInMeters}", distanceInMeters);

  return get(`${url}`, headers)
    .then((result) => {
      return result;
    })
    .catch((error) => error);
};

export const getLocation = async (token: string | null, obj: any) => {
  const headers = postHeaders(token);
  return post(`${clientSettings.urls.endPoints.locationSearch}`, { Location: obj }, headers)
    .then((result) => {
      return result;
    })
    .catch((error) => error);
};

export const getAutoCompleteLocation = async (token: string | null, obj: any) => {
  const headers = postHeaders(token);
  return post(
    `${clientSettings.urls.endPoints.autoCompleteLocationSearch}`,
    { Location: obj },
    headers,
  )
    .then((result) => {
      return result;
    })
    .catch((error) => error);
};

export const getRagNetwork = async (token: string | null, substationNumber: any) => {
  const headers = getHeaders(token);

  let url = clientSettings.urls.endPoints.ragNetworkLoad;
  url = url.replace("{substationNumber}", substationNumber);

  return get(`${url}`, headers)
    .then((result) => {
      return result.data;
    })
    .catch((error) => error);
};

export const loadCrown = async (token: string | null, schemeId: any, schemeVersion: any) => {
  const headers = getHeaders(token);

  let url = clientSettings.urls.endPoints.crownLoad;
  url = url.replace("{schemeId}", schemeId);
  url = url.replace("{schemeVersion}", schemeVersion);

  return get(`${url}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const exportCrown = async (
  token: string | null,
  schemeId: any,
  schemeVersion: any,
  exportData: any,
) => {
  const headers = getHeaders(token);
  let url = clientSettings.urls.endPoints.crownExport;
  url = url.replace("{schemeId}", schemeId);
  url = url.replace("{schemeVersion}", schemeVersion);

  return post(`${url}`, exportData, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const getBillOfMaterials = async (token: string | null, bomData: any) => {
  const headers = getHeaders(token);
  return post(`${clientSettings.urls.endPoints.calculate}`, bomData, headers)
    .then((result) => result.data)
    .catch((error) => {
      return error?.response?.data;
    });
};

export const loadTransformers = async (token: string | null) => {
  const headers = getHeaders(token);
  return get(`${clientSettings?.urls.endPoints.transformerLookUp}`, headers)
    .then((result) => result.data)
    .catch((error) => error);
};

export const getGeoJson = async (bounds: any, layer: any) => {
  const bb = getBounds(bounds);
  const response = await get(
    `${layer.url}&bbox=${bb.southLat},${bb.westLng},${bb.northLat},${bb.eastLng}`,
  );
  const data = await response;
  return data;
};

export const getGeometry = async (token: string | null, bounds: any) => {
  const headers = getHeaders(token);
  const bb = getBounds(bounds);

  const response = await get(
    `${clientSettings.urls.endPoints.layer}?minX=${bb.southLat}&minY=${bb.westLng}&maxX=${bb.northLat}&maxY=${bb.eastLng}`,
    headers,
  );
  const data = await response;
  return data;
};

export const loadExternalStudy = async (token: string | null, id: any) => {
  const headers = getHeaders(token);
  let url = clientSettings.urls.endPoints.importStudy;
  url = url.replace("{id}", id);

  return get(`${url}`, headers)
    .then((result) => {
      return result;
    })
    .catch((error) => error);
};

export const logEvent = async (token: string | null, event: vndEvent) => {
  if (!clientSettings?.features?.SendEventsEnabled || !clientSettings?.urls?.endPoints?.event) {
    return;
  }
  const url = clientSettings.urls.endPoints.event.replace("{type}", event.eventType);
  const headers = postHeaders(token);
  // fire and forget, hence do not await this post
  void post(url, event, headers).catch(() => undefined);
};

export const logUnhandledException = async (token: string | null, loggedException: any) => {
  const headers = postHeaders(token);
  const url = clientSettings.urls.endPoints.logUnhandledException;
  return post(url, loggedException, headers);
};
